import { useEffect as useEffectS, useState as useStateS } from "react";
import { Api } from "./api";
import { Banner, Field, Icon, isCapacitorNative } from "./components";

// Settings / profile screen.
//
// Three tabs are wired to the Flask backend: Profile (full name + email),
// Security & sign-in (change password), and Close account. The remaining
// tabs are visual mock-ups for upcoming features — they show a "Coming
// soon" banner so users don't think Save is broken.

const ROLE_SUBTITLE = {
  super_admin: "SaaS operator",
  company_admin: "Company admin",
  manager: "Community manager",
  homeowner: "Homeowner",
};

const ROLE_AVATAR_COLOR = {
  super_admin: "b",
  company_admin: "b",
  manager: "t",
  homeowner: "g",
};

function initialsFrom(user) {
  const src = (user?.full_name || user?.email || "").trim();
  if (!src) return "??";
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

function displayNameFrom(user) {
  return (user?.full_name || "").trim() || (user?.email || "").split("@")[0];
}

function ComingSoon({ what }) {
  return (
    <Banner icon="info">
      <b>{what} aren't wired up yet.</b> The controls below are a preview.
      Changes here won't persist until this feature ships.
    </Banner>
  );
}

function StatusLine({ status }) {
  if (!status) return null;
  const isError = status.kind === "error";
  return (
    <div role={isError ? "alert" : "status"} style={{
      marginTop: 12, padding: "10px 12px", borderRadius: 8, fontSize: 14,
      background: isError ? "var(--danger-soft, #fdecea)" : "var(--accent-soft, #eef7f1)",
      color: isError ? "var(--danger, #b3261e)" : "var(--accent-ink, #1f4634)",
      border: `1px solid ${isError ? "var(--danger, #b3261e)" : "var(--accent, #2d5d4f)"}`,
    }}>
      {status.msg}
    </div>
  );
}

function ProfilePanel({ user, onUserUpdate }) {
  const [fullName, setFullName] = useStateS(user?.full_name || "");
  const [email, setEmail] = useStateS(user?.email || "");
  const [busy, setBusy] = useStateS(false);
  const [status, setStatus] = useStateS(null);

  // Pick up server-side changes (e.g. a re-fetch from /me elsewhere).
  useEffectS(() => {
    setFullName(user?.full_name || "");
    setEmail(user?.email || "");
  }, [user?.id, user?.full_name, user?.email]);

  const dirty = (fullName !== (user?.full_name || "")) || (email !== (user?.email || ""));

  const onCancel = () => {
    setFullName(user?.full_name || "");
    setEmail(user?.email || "");
    setStatus(null);
  };

  const onSave = async () => {
    if (!dirty || busy) return;
    setStatus(null);
    if (!fullName.trim()) {
      setStatus({ kind: "error", msg: "Full name can't be empty." });
      return;
    }
    setBusy(true);
    try {
      const updated = await Api.updateProfile({
        full_name: fullName.trim(),
        email: email.trim().toLowerCase(),
      });
      onUserUpdate?.(updated);
      setStatus({ kind: "ok", msg: "Profile updated." });
    } catch (err) {
      setStatus({ kind: "error", msg: err.message || "Couldn't update profile." });
    } finally {
      setBusy(false);
    }
  };

  const subtitle = ROLE_SUBTITLE[user?.role] || "Member";
  const community = user?.community?.name;
  const avatarColor = ROLE_AVATAR_COLOR[user?.role] || "g";

  return (
    <div className="card card--pad-lg">
      <div className="row" style={{ gap: 18, marginBottom: 20 }}>
        <span className={`avatar avatar--lg avatar--${avatarColor}`} style={{ width: 64, height: 64, fontSize: 22 }}>
          {initialsFrom(user)}
        </span>
        <div>
          <h2 className="display" style={{ fontSize: 24, fontWeight: 500 }}>{displayNameFrom(user) || "—"}</h2>
          <div className="text-mute">
            {subtitle}{community ? ` · ${community}` : ""}
          </div>
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
        <Field label="Full name">
          <input
            className="input"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            autoComplete="name"
          />
        </Field>
        <Field label="Email">
          <input
            className="input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
        </Field>
      </div>

      <StatusLine status={status} />

      <div className="row" style={{ marginTop: 24, justifyContent: "flex-end", gap: 8 }}>
        <button className="btn btn--ghost" onClick={onCancel} disabled={busy || !dirty}>Cancel</button>
        <button className="btn btn--primary" onClick={onSave} disabled={busy || !dirty}>
          {busy ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

// Linked OAuth accounts (Google / Microsoft). Sits under the same
// "Security & sign-in" tab as the password form because it answers
// the same user question — "how do I sign in to this account?".
//
// The Link / Unlink flows route through the same /api/auth/oauth/*
// endpoints the AuthScreen uses for login; the difference is that
// Link starts authenticated (mints a state token bound to this user)
// while Login starts anonymous. See app/auth/oauth.py for the
// state-token shape.
function LinkedAccountsPanel() {
  const [identities, setIdentities] = useStateS(null);  // null = loading, [] = loaded empty
  const [status, setStatus] = useStateS(null);
  const [confirmingUnlink, setConfirmingUnlink] = useStateS(null);
  const [busy, setBusy] = useStateS(false);

  const refresh = async () => {
    try {
      const data = await Api.oauthListIdentities();
      setIdentities(data.identities || []);
    } catch (err) {
      setStatus({ kind: "error", msg: err.message || "Couldn't load linked accounts." });
      setIdentities([]);
    }
  };

  useEffectS(() => { refresh(); }, []);

  // Catch the post-link bounce: if the user just came back from a
  // successful link redirect (#linked=<provider>), refresh the list
  // so the freshly linked provider shows up without a manual reload.
  // The OAuth-callback page redirects to /?route=settings&settings_tab=security,
  // so by the time we mount the URL is clean — we just refresh once
  // on mount. If a future ergonomic improvement adds a flash banner
  // here, this is the place to hook it.

  const startLink = async (provider) => {
    if (busy) return;
    setBusy(true);
    setStatus(null);
    try {
      const data = await Api.oauthLinkStart(provider);
      if (!data || !data.auth_url) {
        throw new Error("OAuth provider isn't set up.");
      }
      window.location.assign(data.auth_url);
    } catch (err) {
      setBusy(false);
      const msg = (err && err.code === "provider_not_configured")
        ? "That provider isn't configured for this site."
        : (err && err.message) || "Couldn't start linking.";
      setStatus({ kind: "error", msg });
    }
  };

  const doUnlink = async (provider) => {
    setBusy(true);
    setStatus(null);
    try {
      await Api.oauthUnlink(provider);
      setStatus({ kind: "ok", msg: `Unlinked ${provider[0].toUpperCase() + provider.slice(1)}.` });
      setConfirmingUnlink(null);
      await refresh();
    } catch (err) {
      setStatus({ kind: "error", msg: err.message || "Couldn't unlink." });
    } finally {
      setBusy(false);
    }
  };

  // Group ALL linked identities by provider — a user can legitimately
  // have multiple rows per (user, provider) (e.g., personal + work
  // Google) since the backend's unique constraint is on (provider,
  // subject) globally, not on (user_id, provider). Codex P2 on PR
  // #185: the prior render collapsed to one row per provider, hiding
  // any extras and misleading users about what was actually linked.
  const linkedByProvider = {};
  (identities || []).forEach((id) => {
    if (!linkedByProvider[id.provider]) linkedByProvider[id.provider] = [];
    linkedByProvider[id.provider].push(id);
  });

  const PROVIDERS = [
    { key: "google", label: "Google" },
    { key: "microsoft", label: "Microsoft" },
  ];

  return (
    <div className="card card--pad-lg">
      <h3 className="display" style={{ fontSize: 22, fontWeight: 500 }}>Linked sign-in accounts</h3>
      <p className="text-mute text-sm" style={{ marginTop: 4 }}>
        Linking lets you sign in via Google or Microsoft instead of (or in addition to) your password.
        Unlinking removes every identity from that provider; your password still works.
      </p>
      <hr className="divider" />

      <div className="stack-sm">
        {PROVIDERS.map(({ key, label }) => {
          const linkedList = linkedByProvider[key] || [];
          const anyLinked = linkedList.length > 0;
          return (
            <div key={key} className="row" style={{
              alignItems: "flex-start", justifyContent: "space-between",
              gap: 12, padding: "10px 0",
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{label}</div>
                {anyLinked ? (
                  <div className="text-sm text-mute">
                    {linkedList.length === 1
                      ? <>Linked as <span className="mono">{linkedList[0].email}</span></>
                      : (
                        <>
                          <div>Linked as:</div>
                          <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                            {linkedList.map((ident) => (
                              // Row PK from the backend — guaranteed
                              // unique even when two identities share
                              // a linked_at second or an email
                              // (different subjects from the same
                              // provider). Codex P2 on PR #187.
                              <li key={ident.id}>
                                <span className="mono">{ident.email}</span>
                              </li>
                            ))}
                          </ul>
                        </>
                      )
                    }
                  </div>
                ) : (
                  <div className="text-sm text-mute">Not linked.</div>
                )}
              </div>
              <div>
                {anyLinked
                  ? (confirmingUnlink === key
                      ? (
                        <span className="row" style={{ gap: 6 }}>
                          <button
                            className="btn btn--danger"
                            onClick={() => doUnlink(key)}
                            disabled={busy}
                          >
                            {busy ? "Unlinking…" : (linkedList.length > 1 ? `Unlink all ${linkedList.length}` : "Confirm unlink")}
                          </button>
                          <button
                            className="btn btn--ghost"
                            onClick={() => setConfirmingUnlink(null)}
                            disabled={busy}
                          >
                            Cancel
                          </button>
                        </span>
                      )
                      : (
                        <button
                          className="btn btn--ghost"
                          onClick={() => setConfirmingUnlink(key)}
                        >
                          {linkedList.length > 1 ? `Unlink (${linkedList.length})` : "Unlink"}
                        </button>
                      )
                    )
                  : (
                    <button
                      className="btn btn--primary"
                      onClick={() => startLink(key)}
                      disabled={busy}
                    >
                      Link {label}
                    </button>
                  )
                }
              </div>
            </div>
          );
        })}
        {identities === null && (
          <div className="text-mute text-sm">Loading…</div>
        )}
      </div>

      <StatusLine status={status} />
    </div>
  );
}


function SecurityPanel() {
  const [current, setCurrent] = useStateS("");
  const [next, setNext] = useStateS("");
  const [confirm, setConfirm] = useStateS("");
  const [busy, setBusy] = useStateS(false);
  const [status, setStatus] = useStateS(null);

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); };

  const onSave = async () => {
    setStatus(null);
    if (!current || !next || !confirm) {
      setStatus({ kind: "error", msg: "Fill in all three password fields." });
      return;
    }
    // Defer to the shared validator exposed by auth.jsx so the
    // change-password form enforces the same policy as signup/reset.
    // Falls back to a length-only check when auth.jsx hasn't loaded
    // yet (defensive — script order in index.html loads auth.jsx
    // first, so this branch shouldn't fire in practice).
    const _check = typeof window !== "undefined" && window.passwordPolicyError;
    const _pwErr = _check ? _check(next) : (
      next.length < 12
        ? "New password must be at least 12 characters."
        : null
    );
    if (_pwErr) {
      setStatus({ kind: "error", msg: _pwErr });
      return;
    }
    if (next !== confirm) {
      setStatus({ kind: "error", msg: "New password and confirmation don't match." });
      return;
    }
    setBusy(true);
    try {
      await Api.changePassword({ current_password: current, new_password: next });
      reset();
      setStatus({ kind: "ok", msg: "Password changed." });
    } catch (err) {
      setStatus({ kind: "error", msg: err.message || "Couldn't change password." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card card--pad-lg">
      <h3 className="display" style={{ fontSize: 22, fontWeight: 500 }}>Security &amp; sign-in</h3>
      <p className="text-mute text-sm" style={{ marginTop: 4 }}>JWT bearer tokens. Sessions expire after 7 days of inactivity.</p>
      <hr className="divider" />
      <div className="col" style={{ gap: 14 }}>
        <Field label="Current password">
          <input
            className="input" type="password" autoComplete="current-password"
            value={current} onChange={(e) => setCurrent(e.target.value)}
          />
        </Field>
        <Field
          label="New password"
          hint={(typeof window !== "undefined" && window.PASSWORD_HINT) ||
            "At least 12 characters with a letter and a digit or symbol."}
        >
          <input
            className="input" type="password" autoComplete="new-password"
            value={next} onChange={(e) => setNext(e.target.value)}
          />
        </Field>
        <Field label="Confirm new password">
          <input
            className="input" type="password" autoComplete="new-password"
            value={confirm} onChange={(e) => setConfirm(e.target.value)}
          />
        </Field>
      </div>

      <StatusLine status={status} />

      <div className="row" style={{ marginTop: 16, justifyContent: "flex-end", gap: 8 }}>
        <button className="btn btn--ghost" onClick={reset} disabled={busy}>Clear</button>
        <button className="btn btn--primary" onClick={onSave} disabled={busy}>
          {busy ? "Saving…" : "Change password"}
        </button>
      </div>

      <hr className="divider" />
      <div className="row" style={{ justifyContent: "space-between", opacity: 0.65 }}>
        <div>
          <div style={{ fontWeight: 600 }}>Two-factor authentication <span className="pill" style={{ marginLeft: 8, fontSize: 11 }}>Coming soon</span></div>
          <div className="text-sm text-mute">Receive a one-time code on sign-in via authenticator app.</div>
        </div>
        <button className="btn btn--accent" disabled>Enable 2FA</button>
      </div>
      <hr className="divider" />
      <div style={{ opacity: 0.65 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Active sessions <span className="pill" style={{ marginLeft: 8, fontSize: 11 }}>Coming soon</span></div>
        <div className="text-sm text-mute">
          Sessions aren't tracked yet. With JWT, signing out on this device only invalidates this browser's token.
        </div>
      </div>
    </div>
  );
}

// ─── Preferences (notifications + privacy) ────────────────────────────────
// Each row maps to a User.notify_* boolean column and gates a specific
// notification `type` on the backend (see app/services/notify.py
// NOTIFICATION_PREFERENCE_FIELDS). Turning a toggle off stops new
// bell notifications of that type; existing unread ones are left alone.
const NOTIFICATION_ROWS = [
  { field: "notify_messages",      label: "Direct messages",                  channels: "In-app" },
  { field: "notify_events",        label: "New community events",             channels: "In-app" },
  { field: "notify_bylaw_updated", label: "Bylaw updates from your manager",  channels: "In-app" },
  { field: "notify_services",      label: "New neighbor service offers",      channels: "In-app" },
  { field: "notify_assistance",    label: "Neighbors asking for help",        channels: "In-app" },
];

const PRIVACY_ROWS = [
  {
    field: "privacy_show_in_directory",
    label: "Show me in the community directory",
    hint: "Off = your card is hidden from other members. You still see yourself.",
  },
  {
    field: "privacy_bot_remember_context",
    label: "Let Compass remember context across my conversations",
    hint: "Off = each question is sent without prior turns. Useful if you ask unrelated things in a row.",
  },
];

function usePreferences() {
  const [state, setState] = useStateS({ loading: true, error: null, prefs: null });
  useEffectS(() => {
    let cancelled = false;
    window.Api.getPreferences()
      .then((prefs) => { if (!cancelled) setState({ loading: false, error: null, prefs }); })
      .catch((err) => { if (!cancelled) setState({ loading: false, error: err.message || "Couldn't load preferences.", prefs: null }); });
    return () => { cancelled = true; };
  }, []);
  return [state, setState];
}

function PreferencePanel({ title, intro, rows, banner }) {
  const [{ loading, error, prefs }, setState] = usePreferences();
  // Per-row state so toggles can show "Saving…" individually.
  const [pendingField, setPendingField] = useStateS(null);
  const [rowError, setRowError] = useStateS(null);

  const flip = async (field, next) => {
    if (!prefs) return;
    setPendingField(field);
    setRowError(null);
    // Optimistic update so the toggle moves instantly.
    setState((s) => ({ ...s, prefs: { ...s.prefs, [field]: next } }));
    try {
      const updated = await window.Api.updatePreferences({ [field]: next });
      setState((s) => ({ ...s, prefs: updated }));
    } catch (err) {
      // Roll back on failure.
      setState((s) => ({ ...s, prefs: { ...s.prefs, [field]: !next } }));
      setRowError({ field, msg: err.message || "Couldn't save." });
    } finally {
      setPendingField(null);
    }
  };

  return (
    <div className="card card--pad-lg">
      <h3 className="display" style={{ fontSize: 22, fontWeight: 500 }}>{title}</h3>
      {intro && <p className="text-mute text-sm" style={{ marginTop: 4 }}>{intro}</p>}
      {banner}
      <hr className="divider" />
      {loading && <div style={{ color: "var(--mute)", padding: "8px 0" }}>Loading…</div>}
      {error && <div style={{ color: "var(--danger)", padding: "8px 0" }}>{error}</div>}
      {prefs && (
        <div className="col" style={{ gap: 4 }}>
          {rows.map((row, i) => (
            <div key={row.field} className="row" style={{ justifyContent: "space-between", padding: "12px 0", borderBottom: i < rows.length - 1 ? "1px dashed var(--hairline)" : 0 }}>
              <div style={{ flex: 1, paddingRight: 12 }}>
                <div style={{ fontWeight: 500 }}>{row.label}</div>
                {(row.channels || row.hint) && (
                  <div className="text-sm text-mute">{row.channels || row.hint}</div>
                )}
                {rowError && rowError.field === row.field && (
                  <div className="text-xs" style={{ color: "var(--danger)", marginTop: 4 }}>{rowError.msg}</div>
                )}
              </div>
              <Toggle
                on={prefs[row.field]}
                disabled={pendingField === row.field}
                onChange={(next) => flip(row.field, next)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NotificationsPanel() {
  return (
    <PreferencePanel
      title="Notifications"
      intro="Choose what shows up in the bell at the top of the page. Turning a toggle off stops new notifications of that kind from arriving; ones already in your bell stay until you read them."
      rows={NOTIFICATION_ROWS}
    />
  );
}

// ─── Household ────────────────────────────────────────────────────────────
// A household = one billable unit inside an HOA. Primary gets AI
// features (chat + Project Builder); family members get community
// boards only. Primary is transferable so spouses can share the
// AI seat. See pricing.md v10.

function HouseholdPanel() {
  const [state, setState] = useStateS({ loading: true, error: null, data: null });
  const [busy, setBusy] = useStateS(null); // string action id while in-flight
  const [error, setError] = useStateS(null);
  const [inviteEmail, setInviteEmail] = useStateS("");
  const [inviteName, setInviteName] = useStateS("");
  const [lastInvite, setLastInvite] = useStateS(null); // shows the accept link
  const [labelDraft, setLabelDraft] = useStateS("");
  const [editingLabel, setEditingLabel] = useStateS(false);

  const load = () => {
    setState((s) => ({ ...s, loading: true }));
    window.Api.getHousehold()
      .then((data) => {
        setState({ loading: false, error: null, data });
        setLabelDraft(data?.label || "");
      })
      .catch((err) => setState({ loading: false, error: err.message || "Couldn't load household.", data: null }));
  };
  useEffectS(() => { load(); }, []);

  const invite = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    setError(null);
    if (!inviteEmail.trim()) {
      setError("Email is required.");
      return;
    }
    setBusy("invite");
    try {
      const inv = await window.Api.createHouseholdInvite({
        email: inviteEmail.trim(),
        full_name: inviteName.trim() || undefined,
      });
      setLastInvite(inv);
      setInviteEmail("");
      setInviteName("");
      load();
    } catch (err) {
      setError(err.message || "Couldn't create invite.");
    } finally {
      setBusy(null);
    }
  };

  const revokeInvite = async (id) => {
    setBusy(`revoke:${id}`);
    setError(null);
    try {
      await window.Api.deleteHouseholdInvite(id);
      load();
    } catch (err) {
      setError(err.message || "Couldn't revoke invite.");
    } finally {
      setBusy(null);
    }
  };

  const transferPrimary = async (membershipId, name) => {
    if (!window.confirm(`Transfer primary status to ${name || "this member"}? You'll lose AI access; they'll gain it. You can transfer back later.`)) return;
    setBusy(`transfer:${membershipId}`);
    setError(null);
    try {
      const updated = await window.Api.transferHouseholdPrimary(membershipId);
      setState({ loading: false, error: null, data: updated });
    } catch (err) {
      setError(err.message || "Couldn't transfer primary.");
    } finally {
      setBusy(null);
    }
  };

  const removeMember = async (membershipId, name, isOnlyMembership) => {
    // Two confirms — a soft one when the user has other Compass
    // memberships to fall back to, and a heavier one when removal
    // will fully deactivate the account (issue #215). The backend
    // already cascade-cleans marketplace + service-offer + assistance
    // rows on the deactivation path; we tell the primary up-front so
    // there are no surprises.
    const friendly = name || "this member";
    const confirmMsg = isOnlyMembership
      ? (
        `Removing ${friendly} will deactivate their Community Compass `
        + `account entirely (this is their only HOA). Their marketplace `
        + `listings, service offers, and help requests will be removed. `
        + `Messages and Town Square posts stay as history with the `
        + `author shown as "User Deactivated".\n\nA community manager `
        + `can re-activate the account if this was a mistake.\n\n`
        + `Continue?`
      )
      : `Remove ${friendly} from your household? They'll lose access to this HOA.`;
    if (!window.confirm(confirmMsg)) return;
    setBusy(`remove:${membershipId}`);
    setError(null);
    try {
      const updated = await window.Api.removeHouseholdMember(membershipId);
      setState({ loading: false, error: null, data: updated });
      // Refresh /me so the sidebar drops the removed member if the
      // primary's own sub-community membership counts changed (rare
      // but possible if the removed member was sharing the household
      // and the count is part of the payload).
      if (typeof window.Api.me === "function") {
        window.Api.me().catch(() => {});
      }
    } catch (err) {
      setError(err.message || "Couldn't remove member.");
    } finally {
      setBusy(null);
    }
  };

  const saveLabel = async () => {
    setBusy("label");
    setError(null);
    try {
      const updated = await window.Api.updateHousehold({ label: labelDraft.trim() || null });
      setState({ loading: false, error: null, data: updated });
      setEditingLabel(false);
    } catch (err) {
      setError(err.message || "Couldn't save label.");
    } finally {
      setBusy(null);
    }
  };

  // Primary toggles a per-feature flag on a non-primary member.
  // Optimistically updates the local member object so the toggle
  // doesn't visibly snap back if the network is slow.
  const togglePermission = async (membershipId, key, currentValue) => {
    const busyId = `perm:${membershipId}:${key}`;
    setBusy(busyId);
    setError(null);
    try {
      const updatedMember = await window.Api.updateMemberPermissions(membershipId, {
        [key]: !currentValue,
      });
      setState((s) => {
        if (!s.data) return s;
        return {
          ...s,
          data: {
            ...s.data,
            members: s.data.members.map((m) =>
              m.membership_id === updatedMember.membership_id ? updatedMember : m
            ),
          },
        };
      });
    } catch (err) {
      setError(err.message || "Couldn't update permissions.");
    } finally {
      setBusy(null);
    }
  };

  if (state.loading) {
    return <div className="card card--pad-lg" style={{ color: "var(--mute)", textAlign: "center" }}>Loading…</div>;
  }
  if (state.error) {
    return <div className="card card--pad-lg" style={{ color: "var(--danger)" }}>{state.error}</div>;
  }
  if (!state.data) {
    return <div className="card card--pad-lg" style={{ color: "var(--mute)" }}>No household yet.</div>;
  }

  const h = state.data;
  const isPrimary = !!h.viewer_is_primary;
  const familyCount = h.members.filter((m) => !m.is_household_primary).length;
  const pendingCount = h.pending_invites.filter((i) => !i.expired).length;
  const familyTotal = familyCount + pendingCount;
  const familyAtMax = familyTotal >= h.family_max;

  return (
    <div className="card card--pad-lg">
      <h3 className="display" style={{ fontSize: 22, fontWeight: 500 }}>Household</h3>
      <p className="text-mute text-sm" style={{ marginTop: 4 }}>
        Your household is one billable unit in this HOA. The <b>primary</b> account gets AI features
        (chat + Project Builder). <b>Family members</b> use the community boards (events, directory,
        services, assistance, messages). Primary can be transferred at any time.
      </p>

      <hr className="divider" />

      {/* Label */}
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <div className="text-xs text-mute">Household label</div>
          {editingLabel ? (
            <div className="row" style={{ gap: 6, marginTop: 4, alignItems: "center" }}>
              <input
                className="input"
                value={labelDraft}
                maxLength={120}
                placeholder="e.g. The Smith Family"
                onChange={(e) => setLabelDraft(e.target.value)}
                style={{ flex: 1, maxWidth: 320 }}
              />
              <button className="btn btn--primary btn--sm" onClick={saveLabel} disabled={busy === "label"}>
                {busy === "label" ? "Saving…" : "Save"}
              </button>
              <button className="btn btn--ghost btn--sm" onClick={() => { setEditingLabel(false); setLabelDraft(h.label || ""); }}>Cancel</button>
            </div>
          ) : (
            <div className="row" style={{ gap: 8, marginTop: 4, alignItems: "center" }}>
              <div style={{ fontWeight: 600 }}>{h.label || <span className="text-mute">(unnamed)</span>}</div>
              {isPrimary && (
                <button className="btn btn--ghost btn--sm" onClick={() => setEditingLabel(true)}>Edit</button>
              )}
            </div>
          )}
        </div>
      </div>

      <hr className="divider" />

      {/* Members */}
      <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
        <div style={{ fontWeight: 600 }}>Members</div>
        <div className="text-sm text-mute">{familyCount} of {h.family_max} family seats used</div>
      </div>
      <div className="col" style={{ gap: 4 }}>
        {h.members.map((m, i) => (
          <div key={m.membership_id} style={{
            padding: "10px 0",
            borderBottom: i < h.members.length - 1 ? "1px dashed var(--hairline)" : 0,
          }}>
            <div className="row" style={{ justifyContent: "space-between", gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="row" style={{ gap: 8, alignItems: "center" }}>
                  <span style={{ fontWeight: 500 }}>{m.full_name || m.email}</span>
                  {m.is_household_primary && <span className="pill pill--gold" style={{ fontSize: 11 }}>Primary · AI access</span>}
                </div>
                <div className="text-xs text-mute" style={{ marginTop: 2 }}>{m.email}</div>
              </div>
              {isPrimary && !m.is_household_primary && (
                <div className="row" style={{ gap: 6 }}>
                  <button
                    className="btn btn--ghost btn--sm"
                    onClick={() => transferPrimary(m.membership_id, m.full_name)}
                    disabled={busy === `transfer:${m.membership_id}`}
                  >
                    {busy === `transfer:${m.membership_id}` ? "…" : "Make primary"}
                  </button>
                  <button
                    className="btn btn--ghost btn--sm"
                    onClick={() => removeMember(m.membership_id, m.full_name, m.is_only_membership)}
                    disabled={busy === `remove:${m.membership_id}`}
                    style={{ color: "var(--danger)" }}
                  >
                    {busy === `remove:${m.membership_id}` ? "…" : "Remove"}
                  </button>
                </div>
              )}
            </div>
            {/* Per-feature toggles — primary's view of a non-primary
                member. Each pill is a clickable on/off control. */}
            {isPrimary && !m.is_household_primary && m.permissions && (
              <div className="row" style={{
                gap: 6, marginTop: 8, flexWrap: "wrap",
                paddingLeft: 0,
              }}>
                <span className="text-xs text-mute" style={{ alignSelf: "center", marginRight: 4 }}>
                  Allow:
                </span>
                {[
                  ["messages", "Direct messages"],
                  ["town_square", "Town Square"],
                  ["marketplace", "Marketplace"],
                  ["services", "Service offers"],
                  ["assistance", "Help requests"],
                ].map(([key, label]) => {
                  const on = !!m.permissions[key];
                  const busyId = `perm:${m.membership_id}:${key}`;
                  return (
                    <button
                      key={key}
                      type="button"
                      className="pill"
                      onClick={() => togglePermission(m.membership_id, key, on)}
                      disabled={busy === busyId}
                      title={on
                        ? `${label} are enabled. Click to disable.`
                        : `${label} are disabled. Click to enable.`}
                      style={{
                        cursor: "pointer",
                        fontSize: 11,
                        padding: "3px 10px",
                        border: "1px solid var(--hairline)",
                        background: on ? "var(--accent-soft, #e6efe9)" : "var(--bg-2)",
                        color: on ? "var(--ink)" : "var(--mute)",
                        textDecoration: on ? "none" : "line-through",
                        opacity: busy === busyId ? 0.5 : 1,
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pending invites */}
      {h.pending_invites.length > 0 && (
        <>
          <hr className="divider" />
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Pending invites</div>
          <div className="col" style={{ gap: 4 }}>
            {h.pending_invites.map((inv, i) => (
              <div key={inv.id} className="row" style={{
                justifyContent: "space-between",
                padding: "10px 0",
                borderBottom: i < h.pending_invites.length - 1 ? "1px dashed var(--hairline)" : 0,
                gap: 12,
              }}>
                <div>
                  <div style={{ fontWeight: 500 }}>{inv.full_name || inv.email}</div>
                  <div className="text-xs text-mute" style={{ marginTop: 2 }}>
                    {inv.email}{inv.expired ? " · expired" : ""}
                  </div>
                </div>
                {isPrimary && (
                  <button
                    className="btn btn--ghost btn--sm"
                    onClick={() => revokeInvite(inv.id)}
                    disabled={busy === `revoke:${inv.id}`}
                    style={{ color: "var(--danger)" }}
                  >
                    {busy === `revoke:${inv.id}` ? "…" : "Revoke"}
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Invite form (primary only) */}
      {isPrimary && (
        <>
          <hr className="divider" />
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            Invite a family member
          </div>
          {familyAtMax ? (
            <div className="text-sm text-mute">
              Your household has the max of {h.family_max} family seats (including pending invites).
              Remove or revoke one before inviting another.
            </div>
          ) : (
            <form onSubmit={invite} className="col" style={{ gap: 8 }}>
              <div className="row" style={{ gap: 8 }}>
                <input
                  className="input"
                  type="email"
                  placeholder="Email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  style={{ flex: 1 }}
                />
                <input
                  className="input"
                  type="text"
                  placeholder="Name (optional)"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  style={{ flex: 1 }}
                />
                <button type="submit" className="btn btn--primary" disabled={busy === "invite"}>
                  {busy === "invite" ? "Sending…" : "Invite"}
                </button>
              </div>
              <div className="text-xs text-mute">Invites expire after 14 days.</div>
            </form>
          )}
          {lastInvite && lastInvite.accept_path && (
            <div className="card card--pad-lg" style={{ marginTop: 12, background: "var(--accent-soft)" }}>
              <div style={{ fontWeight: 600 }}>Invite created for {lastInvite.email}</div>
              <div className="text-sm" style={{ marginTop: 4 }}>
                Email delivery isn't wired yet, so copy this link and send it to them manually:
              </div>
              <code style={{
                display: "block",
                marginTop: 8,
                padding: 10,
                background: "var(--bg-2)",
                borderRadius: 6,
                fontSize: 12,
                wordBreak: "break-all",
              }}>
                {window.location.origin}{lastInvite.accept_path}
              </code>
            </div>
          )}
        </>
      )}

      {error && (
        <div style={{ marginTop: 12, color: "var(--danger)", fontSize: 14 }}>{error}</div>
      )}
    </div>
  );
}

function PrivacyPanel({ role }) {
  return (
    <PreferencePanel
      title="Privacy"
      intro="These are real and take effect immediately."
      rows={PRIVACY_ROWS}
      banner={role === "homeowner" ? (
        <div style={{ marginTop: 12 }}>
          <Banner icon="shield">
            <b>The contents of your questions are always private.</b> Your community
            manager sees question <i>counts</i> only — never the wording.
          </Banner>
        </div>
      ) : null}
    />
  );
}

// ─── Billing (Stripe) ─────────────────────────────────────────────────────
const TIER_DESCRIPTIONS = {
  trial: "Free trial",
  starter: "Smaller HOAs",
  pro: "Most HOAs",
  enterprise: "Large communities + property groups",
};

function billingReturnUrl(suffix) {
  // Stay on whatever path we're already on so the SPA route is preserved
  // after Stripe redirects back. The query param tells us to re-fetch.
  const url = new URL(window.location.href);
  url.search = "";
  url.searchParams.set("billing", suffix);
  return url.toString();
}

function _fmtDateB(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

// Renders one available subscription option in the BillingPanel.
// Today the only plan is the self-managed per-household one; PMC
// plans will appear here too once their checkout flow ships.
function PlanCard({ plan, projection, unitCount, onUpgrade, pending, disabled }) {
  const isPerUnit = plan.billing_kind === "per_unit";
  const perDollar = (plan.per_unit_cents / 100).toFixed(2);
  const floorDollar = (plan.floor_cents / 100).toFixed(0);
  const ceilingDollar = (plan.ceiling_cents / 100).toFixed(0);

  // Per-unit checkout requires unit_count. Without it, render a
  // gate explaining what to do — clicking Start would otherwise
  // hit a 409 unit_count_required from the backend.
  const needsUnitCount = isPerUnit && (!unitCount || unitCount <= 0);

  return (
    <div className="card" style={{
      padding: 16, border: "1px solid var(--hairline)", background: "var(--surface-2)",
    }}>
      <div style={{ fontWeight: 600, fontSize: 16 }}>{plan.name}</div>
      {isPerUnit && (
        <>
          <div className="text-sm" style={{ marginTop: 6 }}>
            <span className="mono" style={{ fontWeight: 600 }}>${perDollar}</span>
            <span className="text-mute"> per household / month</span>
          </div>
          <div className="text-xs text-mute" style={{ marginTop: 2 }}>
            Minimum <span className="mono">${floorDollar}/mo</span>, maximum <span className="mono">${ceilingDollar}/mo</span> per HOA.
          </div>
        </>
      )}
      {!isPerUnit && (
        <div className="text-sm text-mute" style={{ marginTop: 6 }}>
          {TIER_DESCRIPTIONS[plan.tier] || "Subscription tier"}
        </div>
      )}

      {/* Projection: what THIS community will actually pay */}
      {isPerUnit && projection && (
        <div style={{
          marginTop: 10, padding: 10, borderRadius: 8,
          background: "var(--accent-soft)", color: "var(--accent-ink)",
        }}>
          <div className="text-sm" style={{ fontWeight: 600 }}>
            Your bill: ${(projection.monthly_cents / 100).toFixed(2)} / month
          </div>
          <div className="text-xs" style={{ marginTop: 2, opacity: 0.85 }}>
            {projection.unit_count} household{projection.unit_count === 1 ? "" : "s"}
            {projection.floor_binds && ` · floor binds (counted as ${projection.billable_quantity})`}
            {projection.ceiling_binds && ` · ceiling binds (counted as ${projection.billable_quantity})`}
          </div>
        </div>
      )}

      {/* Unit-count gate */}
      {needsUnitCount && (
        <div style={{
          marginTop: 10, padding: 10, borderRadius: 8,
          background: "var(--bg-2)", fontSize: 13, lineHeight: 1.5,
        }}>
          <b>Set your household count first.</b> The self-managed plan is
          billed per household. Open the manager dashboard and use the
          household count editor, then come back here to subscribe.
        </div>
      )}

      <button
        className="btn btn--accent btn--sm"
        style={{ marginTop: 12, width: "100%" }}
        onClick={onUpgrade}
        disabled={disabled || needsUnitCount}
        title={needsUnitCount ? "Set household count first" : undefined}
      >
        {pending ? "Opening checkout…" : (needsUnitCount ? "Set household count first" : "Subscribe")}
      </button>
    </div>
  );
}


function BillingPanel() {
  const [state, setState] = useStateS({ loading: true, error: null, data: null });
  const [pending, setPending] = useStateS(null);  // "checkout:<price>" or "portal"
  // On the native iOS/Android shell, billing is hidden entirely: no in-app
  // purchases AND no links steering users to an external purchase page
  // (Apple 3.1.1 / 3.1.3). The Billing tab isn't rendered on native; these
  // native guards are defensive no-ops.
  const isNative = isCapacitorNative();
  const [actionError, setActionError] = useStateS(null);
  const [bannerStatus, setBannerStatus] = useStateS(null);  // "success" | "cancel"

  const load = () => {
    setState({ loading: true, error: null, data: null });
    window.Api.billingStatus()
      .then((data) => setState({ loading: false, error: null, data }))
      .catch((err) => setState({ loading: false, error: err.message || "Couldn't load billing status.", data: null }));
  };

  useEffectS(() => {
    load();
    // If we just came back from Stripe, surface a banner and clean the URL.
    const url = new URL(window.location.href);
    const billing = url.searchParams.get("billing");
    if (billing === "success" || billing === "cancel") {
      setBannerStatus(billing);
      url.searchParams.delete("billing");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  const upgrade = async (price_id) => {
    if (isNative) return;  // billing hidden on native (Apple 3.1.1 / 3.1.3)
    setPending(`checkout:${price_id}`);
    setActionError(null);
    try {
      const { url } = await window.Api.billingCheckout({
        price_id,
        success_url: billingReturnUrl("success"),
        cancel_url: billingReturnUrl("cancel"),
      });
      window.location.href = url;
    } catch (err) {
      setActionError(err.message || "Couldn't start checkout.");
      setPending(null);
    }
  };

  const openPortal = async () => {
    if (isNative) return;  // billing hidden on native (Apple 3.1.3)
    setPending("portal");
    setActionError(null);
    try {
      const { url } = await window.Api.billingPortal({ return_url: billingReturnUrl("manage") });
      window.location.href = url;
    } catch (err) {
      setActionError(err.message || "Couldn't open the customer portal.");
      setPending(null);
    }
  };

  return (
    <div className="card card--pad-lg">
      <h3 className="display" style={{ fontSize: 22, fontWeight: 500 }}>Plan &amp; billing</h3>

      {bannerStatus === "success" && (
        <div style={{ marginTop: 12 }}>
          <Banner icon="check" tone="accent">
            <b>Thanks!</b> Your subscription is being activated. It may take a few seconds for the new tier to appear here.
          </Banner>
        </div>
      )}
      {bannerStatus === "cancel" && (
        <div style={{ marginTop: 12 }}>
          <Banner icon="info">
            Checkout cancelled — nothing was charged.
          </Banner>
        </div>
      )}

      {state.loading && <div className="text-mute" style={{ padding: "16px 0" }}>Loading…</div>}
      {state.error && <div style={{ color: "var(--danger)", padding: "16px 0" }}>{state.error}</div>}

      {state.data && !state.data.is_configured && (
        <>
          <hr className="divider" />
          <Banner icon="info">
            <b>Billing isn't configured on this server yet.</b> Once the operator sets <span className="mono">STRIPE_SECRET_KEY</span> and the per-tier price IDs, this tab will let you start, change, or cancel a subscription.
          </Banner>
        </>
      )}

      {state.data && state.data.is_configured && state.data.billing_owner === "pmc" && (
        <>
          <hr className="divider" />
          <Banner icon="info">
            <b>Billed by {state.data.pmc?.name || "your property management company"}.</b>{" "}
            This community is part of a property-management-company subscription.
            The company admin manages billing — there's nothing to set up here.
          </Banner>
        </>
      )}

      {state.data && state.data.is_configured && state.data.billing_owner !== "pmc" && (
        <>
          <hr className="divider" />
          <div className="row" style={{ gap: 18, alignItems: "flex-start", justifyContent: "space-between" }}>
            <div>
              <div className="text-mute text-sm">Current plan</div>
              <div className="row" style={{ gap: 8, marginTop: 4 }}>
                <span className="display" style={{ fontSize: 26, fontWeight: 400 }}>
                  {state.data.tier ? state.data.tier[0].toUpperCase() + state.data.tier.slice(1) : "—"}
                </span>
                {state.data.subscription_status && (
                  <span className="pill pill--ghost mono">{state.data.subscription_status}</span>
                )}
              </div>
              {state.data.current_period_end && (
                <div className="text-sm text-mute" style={{ marginTop: 6 }}>
                  Renews {_fmtDateB(state.data.current_period_end)}
                </div>
              )}
            </div>
            {state.data.has_active_subscription && !isNative && (
              <button className="btn btn--ghost" onClick={openPortal} disabled={pending === "portal"}>
                {pending === "portal" ? "Opening…" : "Manage subscription"}
              </button>
            )}
          </div>

          {actionError && (
            <div role="alert" style={{
              marginTop: 12, padding: "10px 12px", borderRadius: 8, fontSize: 14,
              background: "var(--danger-soft, #fdecea)",
              color: "var(--danger, #b3261e)",
              border: "1px solid var(--danger, #b3261e)",
            }}>{actionError}</div>
          )}

          {!isNative && state.data.available_tiers.length > 0 && !state.data.has_active_subscription && (
            <>
              <hr className="divider" />
              <div className="text-mute text-sm" style={{ marginBottom: 12 }}>Pick a plan to get started.</div>
              <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
                {state.data.available_tiers.map((t) => (
                  <PlanCard
                    key={t.price_id}
                    plan={t}
                    projection={state.data.self_managed_projection}
                    unitCount={state.data.unit_count}
                    onUpgrade={() => upgrade(t.price_id)}
                    pending={pending === `checkout:${t.price_id}`}
                    disabled={pending !== null}
                    actionError={actionError}
                  />
                ))}
              </div>
              <div className="text-xs text-mute" style={{ marginTop: 10 }}>
                Plan switches and cancellations are handled in the Customer Portal once you have an active subscription.
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}


function CloseAccountPanel({ onLogout }) {
  const [password, setPassword] = useStateS("");
  const [confirming, setConfirming] = useStateS(false);
  const [busy, setBusy] = useStateS(false);
  const [status, setStatus] = useStateS(null);

  const onClose = async () => {
    if (!confirming) {
      setConfirming(true);
      setStatus(null);
      return;
    }
    if (!password) {
      setStatus({ kind: "error", msg: "Enter your password to confirm." });
      return;
    }
    setBusy(true);
    try {
      await Api.closeAccount({ password });
      // Session is already cleared by Api.closeAccount; bounce to login.
      onLogout?.();
    } catch (err) {
      setStatus({ kind: "error", msg: err.message || "Couldn't delete account." });
      setBusy(false);
    }
  };

  return (
    <div className="card card--pad-lg" style={{ borderColor: "var(--danger)" }}>
      <h3 className="display" style={{ fontSize: 22, fontWeight: 500, color: "var(--danger)" }}>Delete account</h3>
      <p className="text-mute text-sm" style={{ marginTop: 6 }}>
        Deleting your account is <strong>permanent and cannot be undone</strong>.
        We erase your profile and sign-in (email, name, password, and any linked
        Google, Microsoft, or Apple logins), delete your conversation history, and
        remove your marketplace, service, and assistance posts. Your community's
        bylaws and anonymized usage totals are unaffected. You can sign up again
        later with a new account.
      </p>
      {confirming && (
        <div style={{ marginTop: 14 }}>
          <Field label="Confirm with your password">
            <input
              className="input" type="password" autoComplete="current-password"
              value={password} onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
        </div>
      )}
      <StatusLine status={status} />
      <div className="row" style={{ marginTop: 14, gap: 8 }}>
        {confirming && (
          <button className="btn btn--ghost" onClick={() => { setConfirming(false); setPassword(""); setStatus(null); }} disabled={busy}>
            Cancel
          </button>
        )}
        <button
          className="btn btn--ghost"
          style={{ color: "var(--danger)", borderColor: "var(--danger)" }}
          onClick={onClose}
          disabled={busy}
        >
          {busy ? "Deleting…" : (confirming ? "Yes, permanently delete my account" : "Delete my account")}
        </button>
      </div>
    </div>
  );
}

function Settings({ role, user, onUserUpdate, onLogout, initialTab }) {
  // Billing is hidden on the native shell (Apple 3.1.1 / 3.1.3): no in-app
  // purchases and no links to an external purchase page. Coerce any
  // "billing" deep link (billing/trial notifications, ?settings_tab=billing)
  // to a safe tab on native so the hand-off doesn't land on a blank,
  // suppressed panel.
  const isNative = isCapacitorNative();
  const safeTab = (t) => (isNative && t === "billing" ? "profile" : t);
  const [tab, setTab] = useStateS(safeTab(initialTab || "profile"));
  // If a navigation hand-off changes the initial tab after mount
  // (e.g. clicking a 'Payment failed' notification while already on
  // Settings), respect it.
  useEffectS(() => {
    const next = safeTab(initialTab);
    if (initialTab && next !== tab) setTab(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab]);

  // If app.jsx didn't pass `user` (older invocation), fall back to /me.
  const [fetched, setFetched] = useStateS(null);
  useEffectS(() => {
    if (user || !window.Api?.getToken()) return;
    let cancelled = false;
    window.Api.me().then((u) => { if (!cancelled) setFetched(u); }).catch(() => {});
    return () => { cancelled = true; };
  }, [user]);
  const effectiveUser = user || fetched;

  return (
    <div className="scroll">
      <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 36, maxWidth: 900 }}>
        <nav className="set__nav">
          {[
            ["profile", "Profile"],
            ["household", "Household"],
            ["security", "Security & sign-in"],
            ["notifications", "Notifications"],
            ["privacy", "Privacy"],
            role === "manager" && !isNative && ["billing", "Billing & plan"],
            role === "admin" && ["operator", "Operator settings"],
            ["danger", "Delete account"],
          ].filter(Boolean).map(([id, label]) => (
            <button key={id} className={tab === id ? "is-active" : ""} onClick={() => setTab(id)}>{label}</button>
          ))}
        </nav>

        <div className="stack-md">
          {tab === "profile" && <ProfilePanel user={effectiveUser} onUserUpdate={onUserUpdate} />}

          {tab === "household" && <HouseholdPanel />}

          {tab === "security" && (
            <div className="stack-md">
              <SecurityPanel />
              <LinkedAccountsPanel />
            </div>
          )}

          {tab === "notifications" && <NotificationsPanel />}

          {tab === "privacy" && <PrivacyPanel role={role} />}

          {tab === "billing" && role === "manager" && !isNative && <BillingPanel />}

          {tab === "operator" && role === "admin" && (
            <div className="card card--pad-lg">
              <h3 className="display" style={{ fontSize: 22, fontWeight: 500 }}>Operator settings</h3>
              <hr className="divider" />
              <ComingSoon what="Operator-level controls (model selection, API key rotation, etc.)" />
            </div>
          )}

          {tab === "danger" && <CloseAccountPanel onLogout={onLogout} />}
        </div>
      </div>
    </div>
  );
}

// Controlled toggle — `on` is read directly from the prop on every
// render so a server-rollback (e.g. `flip()` reverts `prefs[row.field]`
// after a failed PATCH) shows up in the UI immediately. The earlier
// version copied the prop into a local useState at mount time, which
// meant prop changes were ignored and the toggle was stuck on the
// click-time value until remount. CODE_REVIEW P2 L263.
function Toggle({ on = false, onChange, disabled = false }) {
  const handle = () => {
    if (disabled) return;
    onChange?.(!on);
  };
  return (
    <button onClick={handle} disabled={disabled} style={{
      width: 38, height: 22, borderRadius: 999,
      background: on ? "var(--accent)" : "var(--hairline-2)",
      position: "relative",
      transition: "background .15s",
      opacity: disabled ? 0.55 : 1,
      cursor: disabled ? "not-allowed" : "pointer",
    }}>
      <span style={{
        position: "absolute", top: 2, left: on ? 18 : 2,
        width: 18, height: 18, borderRadius: "50%",
        background: "#fff",
        transition: "left .15s",
        boxShadow: "0 1px 2px rgba(0,0,0,.2)",
      }} />
    </button>
  );
}

export { Settings };
